import React from "react";
import {
  Box,
  Container,
  SimpleGrid,
  Heading,
  Text,
  Image,
  VStack,
  Badge,
  Button,
  HStack,
  Icon,
  useColorModeValue,
  Flex,
  Progress,
  useToast,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import { Link as RouterLink } from "react-router-dom";
import { FiShoppingBag, FiStar, FiPercent, FiClock, FiShoppingCart } from "react-icons/fi";
import { products } from "../data/products";
import { useCart } from "../context/CartContext";
import type { Product } from "../types";

const MotionBox = motion(Box);

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      duration: 0.5,
    },
  },
};

const Sale: React.FC = () => {
  const { addToCart } = useCart();
  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const toast = useToast();

  const handleAddToCart = (product: Product) => {
    addToCart(product);
    toast({
      title: "Added to cart",
      description: `${product.name} has been added to your cart`,
      status: "success",
      duration: 2000,
      isClosable: true,
      position: "top",
      variant: "subtle",
    });
  };

  // Filter products with discount
  const saleProducts = products.filter((product) => product.discount > 0);

  return (
    <Box py={20} bg={useColorModeValue("gray.50", "gray.900")}>
      <Container maxW="1400px">
        <VStack spacing={12} align="stretch">
          <VStack spacing={4} textAlign="center">
            <Badge colorScheme="green" fontSize="md" px={3} py={1}>
              Special Offers
            </Badge>
            <Heading
              size="2xl"
              bgGradient="linear(to-r, green.500, green.600)"
              bgClip="text"
              fontWeight="bold"
            >
              Deals on Premium Components
            </Heading>
            <Text fontSize="lg" color="gray.600" maxW="800px">
              Get amazing discounts on high-performance computer parts. Limited time
              offers on processors, graphics cards, and more!
            </Text>
          </VStack>

          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={8}>
            {saleProducts.map((product) => (
              <RouterLink
                key={product.id}
                to={`/product/${product.id}`}
                style={{ textDecoration: "none" }}
              >
                <Box
                  bg={bgColor}
                  p={6}
                  borderRadius="lg"
                  borderWidth="1px"
                  borderColor={borderColor}
                  transition="all 0.3s"
                  _hover={{
                    transform: "translateY(-4px)",
                    boxShadow: "lg",
                    borderColor: "green.500",
                  }}
                  cursor="pointer"
                >
                  <VStack spacing={4} align="stretch">
                    <Box position="relative">
                      <Badge
                        colorScheme="green"
                        position="absolute"
                        top={2}
                        right={2}
                        zIndex={1}
                      >
                        {product.discount}% OFF
                      </Badge>
                      <Box
                        as="img"
                        src={product.image}
                        alt={product.name}
                        w="100%"
                        h="200px"
                        objectFit="contain"
                        borderRadius="md"
                      />
                    </Box>
                    <VStack spacing={2} align="start">
                      <Heading size="md">{product.name}</Heading>
                      <Text color="gray.600" noOfLines={2}>
                        {product.description}
                      </Text>
                      <HStack spacing={2}>
                        <Text
                          fontSize="xl"
                          fontWeight="bold"
                          color="green.500"
                        >
                          Rs. {product.price.toLocaleString()}
                        </Text>
                        <Text
                          fontSize="md"
                          textDecoration="line-through"
                          color="gray.500"
                        >
                          Rs. {product.originalPrice.toLocaleString()}
                        </Text>
                      </HStack>
                      <Button
                        colorScheme="green"
                        leftIcon={<Icon as={FiShoppingCart} />}
                        w="100%"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          addToCart(product);
                        }}
                        _hover={{
                          bg: "green.600",
                          transform: "translateY(-1px)",
                          boxShadow: "md",
                        }}
                      >
                        Add to Cart
                      </Button>
                    </VStack>
                  </VStack>
                </Box>
              </RouterLink>
            ))}
          </SimpleGrid>
        </VStack>
      </Container>
    </Box>
  );
};

export default Sale;
